import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import {
  CreateTimesheetPayload,
  DeletedTimesheet,
  SavedTimesheet,
  Timesheet,
  UserWorkedHoursWithStatistics,
} from 'src/models/timesheet.model';
import { addCell, removeCell, updateCell } from './search-user-timesheets.reducer';

type InitialState = {
  loading: boolean;
  success: boolean;
  failure: boolean;
  userTimesheets: SavedTimesheet;
};

const INITIAL_STATE: InitialState = {
  loading: false,
  success: false,
  failure: false,
  userTimesheets: null as unknown as SavedTimesheet,
};

export const createUserTimesheet = createAsyncThunk(
  'user-timesheet/post',
  async (data: CreateTimesheetPayload, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<SavedTimesheet> = await axios.post(`/api/user-timesheet`, data);

      const key = `${data.referenceDate}-${data.commission.id}`;
      const newCell = {
        id: response.data.id,
        user: response.data.user,
        referenceDate: response.data.referenceDate,
        commission: response.data.commission,
        hours: response.data.hours,
      };

      dispatch(addCell({ key, cell: newCell }));

      return response.data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserTimesheet = createAsyncThunk('user-timesheet/put', async (data: Timesheet, { rejectWithValue, dispatch }) => {
  try {
    const response: AxiosResponse<SavedTimesheet> = await axios.put(`/api/user-timesheet/${data.id}`, data);

    const key = `${data.referenceDate}-${data.commissionId}`;
    const newCell = {
      id: response.data.id,
      user: response.data.user,
      referenceDate: response.data.referenceDate,
      commission: response.data.commission,
      hours: response.data.hours,
    };

    dispatch(updateCell({ key, cell: newCell }));

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const deleteUserTimesheet = createAsyncThunk(
  'user-timesheet/delete',
  async (data: DeletedTimesheet, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<UserWorkedHoursWithStatistics> = await axios.delete(`/api/user-timesheet/${data.id}`);

      const key = `${data.referenceDate}-${data.commission.id}`;
      const commissionName = data.commission.commissionName;

      dispatch(removeCell({ key, commissionName }));

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const userTimesheetManagementSlice = createSlice({
  name: 'userTimesheetManagement',
  initialState: INITIAL_STATE,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(createUserTimesheet.pending, (state): InitialState => {
        return {
          ...state,
          loading: true,
          success: false,
          failure: false,
        };
      })
      .addCase(createUserTimesheet.fulfilled, (state, action: PayloadAction<SavedTimesheet>): InitialState => {
        return {
          ...state,
          loading: false,
          success: true,
          failure: false,
          userTimesheets: action.payload,
        };
      })
      .addCase(createUserTimesheet.rejected, (state): InitialState => {
        return {
          ...state,
          loading: false,
          success: false,
          failure: true,
        };
      })
      .addCase(updateUserTimesheet.pending, (state): InitialState => {
        return {
          ...state,
          loading: true,
          success: false,
          failure: false,
        };
      })
      .addCase(updateUserTimesheet.fulfilled, (state, action: PayloadAction<SavedTimesheet>): InitialState => {
        return {
          ...state,
          loading: false,
          success: true,
          failure: false,
          userTimesheets: action.payload,
        };
      })
      .addCase(updateUserTimesheet.rejected, (state): InitialState => {
        return {
          ...state,
          loading: false,
          success: false,
          failure: true,
        };
      })
      .addCase(deleteUserTimesheet.pending, (state): InitialState => {
        return {
          ...state,
          loading: true,
          success: false,
          failure: false,
        };
      })
      .addCase(deleteUserTimesheet.fulfilled, (state): InitialState => {
        return {
          ...state,
          loading: false,
          success: true,
          failure: false,
        };
      })
      .addCase(deleteUserTimesheet.rejected, (state): InitialState => {
        return {
          ...state,
          loading: false,
          success: false,
          failure: true,
        };
      });
  },
});

export default userTimesheetManagementSlice.reducer;
